/**
 * AdminAds — Sponsors & Ads admin console.
 *
 * Two panels:
 *   1) Non-Premium Ads — admin authors 3 rich-text ads (slots 1-3) that
 *      appear below the premium rail. Full editor with image upload,
 *      link, active toggle.
 *   2) Premium Slots — table of the 4 premium slots. Assign / unassign
 *      a user to each slot by @username. The advertiser then edits their
 *      own ad from `/my-ad`. Unassigning preserves their ad content.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { CaretLeft, CaretRight, PencilSimple, Plus, Trash, UploadSimple, X } from "@phosphor-icons/react";

import { api, formatErr } from "@/lib/api";
import { sanitizeAdHtml } from "@/components/SponsorsSidebar";

const PREMIUM_SLOTS = 4;
const NONPREMIUM_SLOTS = 6;
const MAX_IMAGES = 8;

const QUILL_MODULES = {
  toolbar: [
    [{ header: [3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike", "color", "background",
  "list", "link",
];

function emptyDraft(slot) {
  return {
    slot,
    title: "", content: "", link_url: "", link_new_tab: false, is_active: true, images: [],
  };
}

function AdPreviewMini({ draft }) {
  const html = useMemo(() => sanitizeAdHtml(draft.content), [draft.content]);
  const [idx, setIdx] = useState(0);
  useEffect(() => { setIdx(0); }, [draft.images.length]);
  useEffect(() => {
    if (draft.images.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % draft.images.length), 8000);
    return () => clearInterval(t);
  }, [draft.images.length]);
  return (
    <div className="border border-white/10 bg-[#0f0f0f] w-full max-w-[240px]">
      <div className="relative">
        {draft.images.length > 0 && (
          <div className="relative w-full bg-black overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
            {draft.images.map((s, i) => (
              <img key={s + i} src={s} alt="preview"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                style={{ opacity: i === idx ? 1 : 0 }} />
            ))}
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 font-mono text-[8px] tracking-widest uppercase bg-white/10 text-zinc-300 px-1.5 py-0.5">
          Sponsor
        </div>
      </div>
      <div className={`p-3 ${draft.images.length === 0 ? "pt-7" : ""}`}>
        {draft.title && <div className="font-display font-black text-sm text-white truncate mb-1">{draft.title}</div>}
        {html && (
          <div className="h57-splash-body text-xs text-zinc-300 leading-snug" dangerouslySetInnerHTML={{ __html: html }} />
        )}
        {draft.link_url && (
          <div className="mt-2 font-mono text-[10px] text-[#FFD700] tracking-widest uppercase">Click Here →</div>
        )}
      </div>
    </div>
  );
}

function NonPremiumEditor({ initial, onClose, onSaved }) {
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_IMAGES - draft.images.length;
    if (room <= 0) { toast.error(`Max ${MAX_IMAGES} images.`); return; }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files.slice(0, room)) {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/uploads/image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      setDraft((d) => ({ ...d, images: [...d.images, ...uploaded] }));
    } catch (err) { toast.error(formatErr(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const moveImg = (from, to) => {
    if (to < 0 || to >= draft.images.length) return;
    setDraft((d) => {
      const arr = [...d.images]; const [x] = arr.splice(from, 1); arr.splice(to, 0, x);
      return { ...d, images: arr };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        content: draft.content,
        link_url: draft.link_url.trim() || null,
        link_new_tab: !!draft.link_new_tab,
        is_active: !!draft.is_active,
        images: draft.images,
      };
      await api.put(`/admin/ads/nonpremium/${draft.slot}`, payload);
      toast.success(`Sponsor slot ${draft.slot} saved`);
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="admin-ad-modal">
      <div className="h57-surface max-w-4xl w-full my-4 p-5 relative">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white" aria-label="Close" data-testid="admin-ad-modal-close">
          <X size={20} weight="bold" />
        </button>
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Sponsor Slot {draft.slot}</div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-5">
          <div className="space-y-4">
            <div>
              <label className="h57-label">Title (optional)</label>
              <input className="h57-input" value={draft.title} maxLength={120}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                data-testid="admin-ad-title" />
            </div>
            <div>
              <label className="h57-label">Message</label>
              <div className="h57-quill">
                <ReactQuill theme="snow" value={draft.content}
                  onChange={(html) => setDraft({ ...draft, content: html })}
                  modules={QUILL_MODULES} formats={QUILL_FORMATS}
                  placeholder="Write your sponsor copy…" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
              <div>
                <label className="h57-label">Link (whole card is clickable)</label>
                <input className="h57-input" value={draft.link_url} maxLength={500}
                  onChange={(e) => setDraft({ ...draft, link_url: e.target.value })}
                  placeholder="https://…" data-testid="admin-ad-link" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm pb-2">
                <input type="checkbox" checked={draft.link_new_tab}
                  onChange={(e) => setDraft({ ...draft, link_new_tab: e.target.checked })}
                  data-testid="admin-ad-newtab" />
                New tab
              </label>
              <label className="inline-flex items-center gap-2 text-sm pb-2">
                <input type="checkbox" checked={draft.is_active}
                  onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                  data-testid="admin-ad-active" />
                Active
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="h57-label !mb-0">Images ({draft.images.length}/{MAX_IMAGES})</label>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="admin-ad-file" />
                <button type="button" onClick={() => fileRef.current?.click()}
                  disabled={uploading || draft.images.length >= MAX_IMAGES}
                  data-testid="admin-ad-upload"
                  className="h57-btn-secondary !py-1.5 !px-2 text-xs disabled:opacity-40">
                  <UploadSimple size={12} weight="bold" className="inline mr-1" />
                  {uploading ? "Uploading…" : "Add"}
                </button>
              </div>
              {draft.images.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {draft.images.map((src, i) => (
                    <div key={src + i} className="relative w-20 h-20 shrink-0 border border-white/10 bg-black group">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-black/70 flex flex-col items-center justify-center gap-1">
                        <div className="flex gap-1">
                          <button type="button" onClick={() => moveImg(i, i-1)} disabled={i===0} className="w-5 h-5 bg-white/10 disabled:opacity-30 text-white flex items-center justify-center"><CaretLeft size={10} weight="bold"/></button>
                          <button type="button" onClick={() => moveImg(i, i+1)} disabled={i===draft.images.length-1} className="w-5 h-5 bg-white/10 disabled:opacity-30 text-white flex items-center justify-center"><CaretRight size={10} weight="bold"/></button>
                        </div>
                        <button type="button" onClick={() => setDraft((d) => ({ ...d, images: d.images.filter((_,idx)=>idx!==i) }))} className="text-red-400 text-[9px] font-mono uppercase"><Trash size={9} weight="bold" className="inline mr-0.5"/>del</button>
                      </div>
                      <div className="absolute top-0.5 left-0.5 font-mono text-[8px] bg-black/70 text-white px-1">{i+1}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">→ Preview</div>
            <AdPreviewMini draft={draft} />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} data-testid="admin-ad-cancel" className="h57-btn-secondary !py-2 !px-3 text-xs">Cancel</button>
          <button type="button" onClick={save} disabled={saving || uploading} data-testid="admin-ad-save" className="h57-btn-primary !py-2 !px-3 text-xs disabled:opacity-40">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminAds() {
  const [ads, setAds] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSlot, setEditSlot] = useState(null); // {slot, ad}

  // Premium slot assignment state
  const [assignSlot, setAssignSlot] = useState(1);
  const [assignUsername, setAssignUsername] = useState("");
  const [assigning, setAssigning] = useState(false);

  // Ad inquiries queue
  const [inquiries, setInquiries] = useState([]);
  const [inquiryCounts, setInquiryCounts] = useState({});
  const [inquiryFilter, setInquiryFilter] = useState("new");
  const [expandedInquiry, setExpandedInquiry] = useState(null);

  const loadInquiries = useCallback(async (status) => {
    try {
      const params = {};
      if (status && status !== "all") params.status = status;
      const { data } = await api.get("/admin/ad-inquiries", { params });
      setInquiries(data.inquiries || []);
      setInquiryCounts(data.counts || {});
    } catch (e) { toast.error(formatErr(e)); }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [adsRes, usersRes] = await Promise.all([
        api.get("/admin/ads"),
        api.get("/admin/users", { params: { limit: 500 } }).catch(() => ({ data: { users: [] } })),
      ]);
      setAds(adsRes.data.ads || []);
      setUsers(usersRes.data.users || []);
      await loadInquiries(inquiryFilter);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [loadInquiries, inquiryFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { loadInquiries(inquiryFilter); }, [inquiryFilter, loadInquiries]);

  const updateInquiry = async (id, patch) => {
    try {
      await api.patch(`/admin/ad-inquiries/${id}`, patch);
      loadInquiries(inquiryFilter);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/admin/ad-inquiries/${id}`);
      setExpandedInquiry(null);
      loadInquiries(inquiryFilter);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const nonpremium = useMemo(
    () => Array.from({ length: NONPREMIUM_SLOTS }, (_, i) =>
      ads.find((a) => a.type === "nonpremium" && a.slot === i + 1) || null,
    ),
    [ads],
  );
  const premium = useMemo(
    () => Array.from({ length: PREMIUM_SLOTS }, (_, i) =>
      ads.find((a) => a.type === "premium" && a.slot === i + 1) || null,
    ),
    [ads],
  );

  const premiumUsers = useMemo(
    () => users.filter((u) => u.is_premium_advertiser),
    [users],
  );

  const startEdit = (slot) => {
    const existing = nonpremium[slot - 1];
    setEditSlot({
      slot,
      ad: existing
        ? {
            slot,
            title: existing.title || "",
            content: existing.content || "",
            link_url: existing.link_url || "",
            link_new_tab: !!existing.link_new_tab,
            is_active: !!existing.is_active,
            images: existing.images || [],
          }
        : emptyDraft(slot),
    });
  };

  const deleteSlot = async (slot) => {
    if (!window.confirm(`Delete sponsor slot ${slot}?`)) return;
    try {
      await api.delete(`/admin/ads/nonpremium/${slot}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const assign = async () => {
    const uname = assignUsername.replace(/^@/, "").trim().toLowerCase();
    if (!uname) { toast.error("Enter a @username"); return; }
    const target = users.find((u) => (u.username || "").toLowerCase() === uname);
    if (!target) { toast.error(`No user @${uname}`); return; }
    setAssigning(true);
    try {
      await api.post(`/admin/users/${target.id}/premium-advertiser`, { slot: assignSlot });
      toast.success(`@${target.username} assigned to Premium Slot ${assignSlot}`);
      setAssignUsername("");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setAssigning(false); }
  };

  const unassign = async (u) => {
    if (!window.confirm(`Remove premium advertiser status from @${u.username}?\n\nTheir ad content will be preserved (hidden) in case you re-assign them later.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}/premium-advertiser`);
      toast.success(`@${u.username} removed`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="mt-6 space-y-6" data-testid="admin-ads-panel">
      {/* Header */}
      <div className="h57-surface p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Sponsors & Ads</div>
        <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
          The Browse page carries a right-rail ad column: <span className="text-white font-semibold">{PREMIUM_SLOTS} premium slots</span> (user-managed,
          always visible) followed by <span className="text-white font-semibold">{NONPREMIUM_SLOTS} sponsor slots</span> (admin-managed).
          Only 3 sponsor slots show at a time — they rotate every 12 seconds. Premium advertisers manage their own ads
          via <span className="text-[#FFD700]">/my-ad</span> after you assign them a slot below.
        </p>
      </div>

      {/* Non-Premium (admin-managed) */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">→ Non-Premium Sponsor Slots</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {nonpremium.map((ad, i) => {
            const slot = i + 1;
            return (
              <div key={slot} className="h57-surface p-3" data-testid={`admin-nonpremium-slot-${slot}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Slot {slot}</div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => startEdit(slot)} data-testid={`admin-nonpremium-edit-${slot}`}
                      className="h57-chip !py-1 !px-2 !text-[10px] hover:!border-[#FFD700] hover:!text-[#FFD700]">
                      <PencilSimple size={11} weight="bold" /> {ad ? "Edit" : "Create"}
                    </button>
                    {ad && (
                      <button type="button" onClick={() => deleteSlot(slot)} data-testid={`admin-nonpremium-delete-${slot}`}
                        className="h57-chip !py-1 !px-2 !text-[10px] !border-red-500/50 !text-red-400 hover:!border-red-500">
                        <Trash size={11} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
                {ad ? (
                  <div>
                    {ad.images && ad.images[0] && (
                      <img src={ad.images[0]} alt="" className="w-full h-24 object-cover mb-2 border border-white/10" />
                    )}
                    <div className="font-display font-black text-sm text-white truncate">{ad.title || "(untitled)"}</div>
                    <div className="text-xs text-zinc-500 truncate mt-0.5">{ad.link_url || "(no link)"}</div>
                    <div className="mt-1">
                      {ad.is_active
                        ? <span className="h57-chip h57-chip-orange !py-0.5 !px-1.5 !text-[9px]">ACTIVE</span>
                        : <span className="h57-chip !py-0.5 !px-1.5 !text-[9px] opacity-60">HIDDEN</span>}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-zinc-600 italic py-4 text-center">Empty — click Create</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Premium slot assignment */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">→ Premium Slots (user-managed)</div>

        <div className="h57-surface p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {premium.map((ad, i) => {
              const slot = i + 1;
              return (
                <div key={slot} className="border border-white/10 p-3" data-testid={`admin-premium-slot-${slot}`}>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">Premium Slot {slot}</div>
                  {ad ? (
                    <div className="mt-1">
                      <div className="text-sm text-white font-semibold truncate">@{ad.owner_username || "—"}</div>
                      <div className="text-xs text-zinc-500 truncate">{ad.title || "(no title)"}</div>
                      <div className="mt-1">
                        {ad.is_active
                          ? <span className="h57-chip h57-chip-orange !py-0.5 !px-1.5 !text-[9px]">LIVE</span>
                          : <span className="h57-chip !py-0.5 !px-1.5 !text-[9px] opacity-60">HIDDEN</span>}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-zinc-600 italic">Unassigned</div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-2 items-end pt-2 border-t border-white/5">
            <div>
              <label className="h57-label">Slot</label>
              <select
                className="h57-input"
                value={assignSlot}
                onChange={(e) => setAssignSlot(Number(e.target.value))}
                data-testid="admin-premium-assign-slot"
              >
                {Array.from({ length: PREMIUM_SLOTS }, (_, i) => (
                  <option key={i} value={i + 1}>{`Slot ${i + 1}`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="h57-label">Assign @username</label>
              <input
                className="h57-input"
                value={assignUsername}
                onChange={(e) => setAssignUsername(e.target.value)}
                placeholder="triodad78"
                data-testid="admin-premium-assign-username"
              />
            </div>
            <button
              type="button"
              onClick={assign}
              disabled={assigning}
              data-testid="admin-premium-assign-btn"
              className="h57-btn-primary !py-2 !px-3 text-xs disabled:opacity-40"
            >
              <Plus size={13} weight="bold" className="inline mr-1" />
              {assigning ? "Assigning…" : "Assign"}
            </button>
          </div>

          {premiumUsers.length > 0 && (
            <div className="pt-3 border-t border-white/5 space-y-1.5">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Current Premium Advertisers</div>
              {premiumUsers.map((u) => (
                <div key={u.id} className="flex items-center justify-between border border-white/10 px-3 py-2" data-testid={`admin-premium-user-${u.username}`}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-white font-semibold">@{u.username}</span>
                    <span className="font-mono text-[10px] text-[#FF5F1F]">Slot {u.premium_ad_slot ?? "—"}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => unassign(u)}
                    data-testid={`admin-premium-unassign-${u.username}`}
                    className="h57-chip !py-1 !px-2 !text-[10px] !border-red-500/50 !text-red-400 hover:!border-red-500"
                  >
                    <Trash size={11} weight="bold" /> Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && <div className="text-xs text-zinc-500">Loading…</div>}

      {/* -------- Ad inquiries queue -------- */}
      <div>
        <div className="flex items-baseline justify-between mb-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">→ Ad Inquiries</div>
          <div className="flex gap-1 flex-wrap">
            {["new", "contacted", "converted", "closed", "all"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setInquiryFilter(s)}
                data-testid={`admin-inquiry-filter-${s}`}
                className={`h57-chip !py-1 !px-2 !text-[10px] uppercase ${inquiryFilter === s ? "!border-[#FFD700] !text-[#FFD700]" : ""}`}
              >
                {s}
                {s !== "all" && inquiryCounts[s] ? ` (${inquiryCounts[s]})` : ""}
              </button>
            ))}
          </div>
        </div>

        <div className="h57-surface p-3">
          {inquiries.length === 0 ? (
            <div className="py-6 text-center text-zinc-500 text-xs font-mono uppercase tracking-widest">
              No {inquiryFilter === "all" ? "" : inquiryFilter} inquiries
            </div>
          ) : (
            <div className="space-y-2" data-testid="admin-inquiries-list">
              {inquiries.map((q) => {
                const isOpen = expandedInquiry === q.id;
                const slot = q.slot_type === "either"
                  ? "Any"
                  : `${q.slot_type === "premium" ? "Premium" : "Sponsor"}${q.slot_number ? ` #${q.slot_number}` : ""}`;
                return (
                  <div key={q.id} className="border border-white/10" data-testid={`admin-inquiry-${q.id}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedInquiry(isOpen ? null : q.id)}
                      className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-white/5 transition-colors"
                    >
                      <span className={`h57-chip !py-0.5 !px-1.5 !text-[9px] ${
                        q.status === "new" ? "!border-[#FF5F1F] !text-[#FF5F1F]" :
                        q.status === "converted" ? "!border-emerald-400/50 !text-emerald-400" :
                        q.status === "closed" ? "opacity-50" : ""
                      }`}>
                        {q.status.toUpperCase()}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-black text-sm text-white truncate">
                          {q.name} <span className="text-zinc-500 font-normal">· {q.email}</span>
                        </div>
                        <div className="font-mono text-[10px] text-zinc-500 truncate">
                          {slot} · {q.business_name || "no business"} · @{q.user_username}
                        </div>
                      </div>
                      <div className="font-mono text-[9px] text-zinc-500 shrink-0">
                        {new Date(q.created_at).toLocaleDateString()}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="border-t border-white/10 p-4 space-y-3 bg-black/30">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          <div><span className="text-zinc-500">Name:</span> <span className="text-white">{q.name}</span></div>
                          <div><span className="text-zinc-500">Email:</span> <a href={`mailto:${q.email}`} className="text-[#FFD700] hover:text-[#FF5F1F]">{q.email}</a></div>
                          <div><span className="text-zinc-500">Phone:</span> <span className="text-white">{q.phone || "—"}</span></div>
                          <div><span className="text-zinc-500">Business:</span> <span className="text-white">{q.business_name || "—"}</span></div>
                          <div><span className="text-zinc-500">Slot interest:</span> <span className="text-white">{slot}</span></div>
                          <div><span className="text-zinc-500">Submitted by:</span> <span className="text-white">@{q.user_username}</span></div>
                        </div>
                        <div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Message</div>
                          <div className="text-sm text-zinc-200 whitespace-pre-wrap border-l-2 border-[#FF5F1F]/40 pl-3">
                            {q.message}
                          </div>
                        </div>
                        <div>
                          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Admin notes</div>
                          <textarea
                            defaultValue={q.admin_notes || ""}
                            rows={2}
                            className="h57-input text-xs"
                            data-testid={`admin-inquiry-notes-${q.id}`}
                            onBlur={(e) => {
                              const v = e.target.value.trim();
                              if ((v || null) !== (q.admin_notes || null)) {
                                updateInquiry(q.id, { admin_notes: v });
                              }
                            }}
                            placeholder="Internal notes (auto-saves on blur)…"
                          />
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {["new", "contacted", "converted", "closed"].map((s) => (
                            <button
                              key={s}
                              type="button"
                              onClick={() => updateInquiry(q.id, { status: s })}
                              data-testid={`admin-inquiry-set-${s}-${q.id}`}
                              className={`h57-chip !py-1 !px-2 !text-[10px] uppercase ${q.status === s ? "!border-[#FFD700] !text-[#FFD700]" : ""}`}
                              disabled={q.status === s}
                            >
                              Mark {s}
                            </button>
                          ))}
                          <div className="flex-1" />
                          <button
                            type="button"
                            onClick={() => deleteInquiry(q.id)}
                            data-testid={`admin-inquiry-delete-${q.id}`}
                            className="h57-chip !py-1 !px-2 !text-[10px] !border-red-500/50 !text-red-400 hover:!border-red-500"
                          >
                            <Trash size={11} weight="bold" /> Delete
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editSlot && (
        <NonPremiumEditor
          initial={editSlot.ad}
          onClose={() => setEditSlot(null)}
          onSaved={() => { setEditSlot(null); load(); }}
        />
      )}
    </div>
  );
}
